<template>
  <DepositAccountCard
    :status="status"
    :header-title="headerTitle"
    :header-text="headerText"
    :items="items"
    :logo-text="translate('depositAccountCard.instabank.image text')"
    :should-show-delete-button="shouldShowDeleteButton"
    :is-deleting-deposit="isDeletingDeposit"
    :should-show-sign-button="shouldShowSignButton"
    :should-show-termination-button="shouldShowTerminationButton"
    delete-button-text="Slett"
    :sign-button-text="translate('depositAccountCard.instabank.button.sign')"
    :terminate-button-text="terminateButtonText"
    :logo="logo"
    @delete="deleteDeposit"
    @sign="openSigningURL"
    @terminate="goToTerminationView"
  >
    <template v-if="status !== 'paid'" v-slot:contentText>
      <template v-if="status === 'ordered'">
        <p>Leietaker har mottatt e-post om å signere kontoavtalen.</p>

        <p>
          Så snart begge parter har signert vil du motta en bekreftelse, og leietaker mottar
          en e-post med innbetalingsinformasjon.
        </p>
      </template>

      <template v-else-if="status === 'terminationExpired'">
        <p>
          Avslutningen har nå utløpt og leietaker kan ikke avslå eller akseptere lenger.
        </p>
            
        <p>For å fortsette så må en ny avslutning startes ved å bruke knappen under.</p>
      </template>

      <template v-else-if="status === 'terminationAcceptedSplit'">
        <p>
          Leietaker har godkjent ditt krav på kr {{ deposit.termination?.splitAmount }}. Når begge
          parter har signert vil pengene bli utbetalt av banken i løpet av 1-3 virkedager, og
          status vil endre seg til "Avsluttet".
        </p>

        <p>
          OBS! Hvis du ikke har mottatt utbetaling etter 4 virkedager, bør du ta kontakt med
          leietaker og verifisere at de har signert med BankID på link mottatt på e-post.
        </p>
      </template>

      <template v-else-if="status === 'terminationDeclined'">
        <p>Leietaker har mottatt en e-post med ditt oppgitte krav.</p>

        <p>
          Leietaker har avslått dette kravet. Du bør kontakte leietaker for å komme frem til
          en enighet, eller ta saken videre til HTU.
          <a
            href="https://www.htu.no/klage-og-tilsvar/send-inn-klage"
            rel="noopener"
            target="_blank"
          >
            Se her for mer informasjon
          </a>
        </p>

        <p>
          Dersom dere kommer frem til en enighet kan du påbegynne avslutning på ny ved å trykke
          på knappen under.
        </p>
      </template>

      <template v-else-if="status === 'terminationAccepted'">
        <p>
          Leietaker har besvart henvendelse om å avslutte depositumskonto for deres
          leieforhold. Når leietaker har signert med BankID vil det gå 1-3 virkedager før
          depositumskontoen blir utbetalt, og status vil da endre seg til "Avsluttet".
        </p>

        <p>
          OBS! Dersom det har gått 4 virkedager og leietaker ikke har mottatt pengene, bør
          leietaker kontrollere at de har signert med BankID på link mottatt over e-post.
        </p>
      </template>

      <template v-else-if="status === 'terminationStartedSplit'">
        <p>Leietaker har mottatt e-post om avslutning av depositumskontoen.</p>
                
        <p>
          Du har gjort krav på kr {{ deposit.termination?.splitAmount }}, og leietaker er
          informert om dette. Dersom leietaker avslår kravet vil du få beskjed.
        </p>
      </template>

      <template v-else-if="status === 'terminationStarted'">
        <p>
          Din leietaker har mottatt en e-post om avslutning av depositumskontoen. Når
          leietakeren har registrert kontonummeret for utbetaling og signert med
          BankID, vil kontoen være avsluttet. Pengene forventes deretter å bli utbetalt
          til leietakeren innen 1-3 virkedager.
        </p>
      </template>

      <template v-else-if="status === 'partiallyPaid'">
        <p>
          Det er gjort innskudd på depositumskontoen. Dette beløpet samsvarer ikke med det
          som står oppført i kontoavtalen.
        </p>

        <p>
          Dersom dere har blitt enige om et nytt beløp så behøver du ikke å utføre noe
          handling.
        </p>
      </template>

      <template v-else-if="status === 'signed'">
        <p>Depositumskonto er signert.</p> 

        <p>
          Så snart vi har fått bekreftelse fra banken på at depositum er innbetalt av
          leietaker, vil du motta en e-post om dette, og saldo vil oppdateres i denne
          visningen.
        </p>
      </template>

      <template v-else-if="status === 'ended'">
        <p>Depositumskonto er avsluttet korrekt.</p>

        <p>
          Banken har utbetalt depositum på konto(er) som ble oppgitt i avslutningen. Merk at
          det kan ta 1-3 virkedager fra utbetaling før midler er disponibel på konto.
        </p>
      </template>
    </template>
  </DepositAccountCard>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import i18nCommon from '@modules/i18n/mixins/Common';
import DepositAccountCard from '@/components/deposit/depositAccount/DepositAccountCard.vue';
import { TenancyApi } from '@modules/husleieapi2';

export default Vue.extend({
  name: 'InstabankCardContainer',
  data() {
    return {
      isDeletingDeposit: false,
    };
  },
  props: {
    landlordId: {
      type: Number,
      required: true,
    },
    tenancyId: {
      type: Number,
      required: true,
    },
    deposit: {
      type: Object,
      required: true,
    },
  },
  mixins: [i18nCommon],
  components: {
    DepositAccountCard,
  },
  computed: {
    ...mapGetters(['isUserSudo']),
    ...mapGetters('tenancy', ['getTenancy']),

    status() {
      const { status, amount, meta, termination } = this.deposit;
      const { answered, accepted, splitAmount, daysUntilTerminationExpires } = termination || {};

      if (['new', 'pending'].includes(status)) {
        return 'ordered';
      }
      if (status === 'ended') {
        return 'ended';
      }
      if (termination && answered === 1 && accepted === 0) {
        return 'terminationDeclined';
      }
      if (termination && answered === 0 && daysUntilTerminationExpires === 0) {
        return 'terminationExpired';
      }
      if (termination && answered === 1 && accepted === 1 && splitAmount > 0) {
        return 'terminationAcceptedSplit';
      }
      if (termination && answered === 1 && accepted === 1) {
        return 'terminationAccepted';
      }
      if (termination && splitAmount > 0) {
        return 'terminationStartedSplit';
      }
      if (termination) {
        return 'terminationStarted';
      }
      if (amount && amount < meta.agreedAmount) {
        return 'partiallyPaid';
      }
      if (amount >= meta.agreedAmount) {
        return 'paid';
      }
      if (status === 'active') {
        return 'signed';
      }
      return undefined;
    },
    headerTitle() {
      return {
        ordered: 'Bestilt',
        terminationExpired: 'Avslutning utløpt',
        terminationAcceptedSplit: 'I avslutningsprosess',
        terminationDeclined: 'Krav avslått',
        terminationAccepted: 'I avslutningsprosess',
        terminationStartedSplit: 'Avslutning påbegynt',
        terminationStarted: 'Avslutning påbegynt',
        partiallyPaid: 'Delvis innbetalt',
        paid: 'Innbetalt',
        signed: 'Signert',
        ended: 'Avsluttet',
      }[this.status] || '';
    },
    headerText() {
      return {
        ordered: 'Depositumskonto er bestilt',
        terminationExpired: 'Avslutning må startes på nytt',
        terminationAcceptedSplit: 'Leietaker har godkjent ditt krav',
        terminationDeclined: 'Leietaker har valgt å avslå ditt krav mot depositum',
        terminationAccepted: 'Leietaker har godkjent avslutning',
        terminationStartedSplit: 'Leietaker har mottatt e-post om avslutning av depositumskonto',
        terminationStarted: 'Leietaker har mottatt e-post om avslutning av depositumskonto',
        partiallyPaid: 'Innbetalt beløp samsvarer ikke med avtalt beløp',
        paid: 'Det er gjort en innbetaling på depositumskonto som stemmer etter deres avtale.',
        signed: 'Depositumskontoen er signert og venter på innbetaling',
        ended: 'Depositumskonto er avsluttet',
      }[this.status] || '';
    },
    items() {
      const { termination } = this.deposit;

      return [
        {
          title: this.translate('depositAccountCard.instabank.items.balance'),
          value: this.toCurrency('NOK', Math.ceil(this.deposit.amount)),
          icon: 'budicon-finance-project',
        },
        {
          title: this.translate('depositAccountCard.instabank.items.agreed deposit'),
          value: this.toCurrency('NOK', Math.ceil(this.deposit.meta.agreedAmount)),
          icon: 'budicon-savings',
        },
        this.status !== 'ordered' ? {
          title: this.translate('depositAccountCard.instabank.items.kid'),
          value: this.deposit.meta.structuredReference,
          icon: 'budicon-cheque',
          letterSpacing: true,
        } : undefined,
        this.status !== 'ordered' ? {
          title: this.translate('depositAccountCard.instabank.items.account number'),
          value: this.deposit.meta.accountNo,
          icon: 'budicon-bank',
        } : undefined,
        termination && termination.answered === 0 ? {
          title: 'Avslutningen må gjennomføres innen',
          value: termination.daysUntilTerminationExpires > 0 ? `${termination.daysUntilTerminationExpires} dag(er)` : 'Utløpt',
          icon: 'budicon-hourglass',
        } : undefined,
      ].filter(item => item);
    },
    shouldShowDeleteButton() {
      return ['draft', 'initiated', 'created'].includes(this.deposit.status);
    },
    shouldShowTerminationButton() {
      const hasDDG = this.getTenancy.subscription.subscriptionPlanName === 'Garantert';

      if (hasDDG && this.isUserSudo) {
        return true;
      }

      return (
        !hasDDG &&
        [
          'paid',
          'partiallyPaid',
          'terminationStartedSplit',
          'terminationAcceptedSplit',
          'terminationStarted',
          'terminationAccepted',
          'terminationDeclined',
          'terminationExpired',
        ].includes(this.status)
      );
    },
    shouldShowSignButton() {
      const { landlordSignUrl } = this.deposit.termination || {};
      const statuses = ['terminationStartedSplit', 'terminationAcceptedSplit'];

      return landlordSignUrl && statuses.includes(this.status) ? true : false;
    },
    terminateButtonText() {
      if (
        ['terminationStarted', 'terminationStartedSplit', 'terminationAcceptedSplit', 'terminationAccepted', 'terminationDeclined'].includes(
          this.status
        )
      ) {
        return this.translate('depositAccountCard.instabank.button.terminate anew');
      }

      return this.translate('depositAccountCard.instabank.button.terminate');
    },
    logo() {
      return require('@/assets/instabank.svg');
    },
  },
  methods: {
    openSigningURL() {
      window.open(this.deposit.termination.landlordSignUrl, '_blank');
    },
    async deleteDeposit() {
      this.isDeletingDeposit = true;

      try {
        const api = new TenancyApi();
        await api.deleteLandlordTenancyDeposit(this.landlordId, this.tenancyId, this.deposit.id);

        window.location.reload();
      } catch {
        this.$toast(
          'En feil oppstod med å slette depositum kontoen. Vennligst prøv igjen.',
          'danger',
        );
      } finally {
        this.isDeletingDeposit = false;
      }
    },
    goToTerminationView() {
      this.$router.push({
        name: 'tenancy_deposit_release_form_instabank',
        params: {
          depId: this.deposit.id,
        },
      });
    },
  },
});
</script>
